import forge from "node-forge";
import axios from "../api/baseurl";

let rsaKeyPair = null;
let rsaPublicKey = null;
let rsaPrivateKey = null;
let rsaKeyReady = false;

// Generate RSA Key Pair as a Promise
const generateRsaKeyPair = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: "RSA-OAEP",
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: "SHA-256",
        },
        true,
        ["encrypt", "decrypt"]
      );

      const publicKeyBuffer = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
      const privateKeyBuffer = await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey);

      rsaKeyPair = keyPair;
      rsaPublicKey = convertArrayBufferToPem(publicKeyBuffer, "PUBLIC KEY");
      rsaPrivateKey = convertArrayBufferToPem(privateKeyBuffer, "PRIVATE KEY").trim();
      rsaKeyReady = true;

      resolve({ rsaPublicKey, rsaPrivateKey });
    } catch (error) {
      console.error("Error generating RSA key pair:", error);
      reject(error);
    }
  });
};

// Ensure RSA keys are generated before proceeding
const ensureRsaKeyReady = async () => {
  if (!rsaKeyReady) {
    console.log("Generating RSA keys...");
    await generateRsaKeyPair();
    console.log("RSA keys generated and ready");
  }
};

// Convert ArrayBuffer to PEM format
const convertArrayBufferToPem = (buffer, keyType) => {
  const byteArray = new Uint8Array(buffer);
  const base64String = window.btoa(String.fromCharCode(...byteArray));
  const formattedKey = base64String.match(/.{1,64}/g).join("\n");
  return `-----BEGIN ${keyType}-----\n${formattedKey}\n-----END ${keyType}-----`;
};

// Fetch Secret Key
const fetchSecretKey = async () => {
  try {
    await ensureRsaKeyReady(); // Wait until RSA keys are ready

    const credentials = `${process.env.REACT_APP_SUPER_USERNAME}:${process.env.REACT_APP_SUPER_PASSWORD}`;
    const base64Credentials = window.btoa(credentials);

    const response = await axios.post(
      "/api/encryption/secretkey",
      { publicKey: rsaPublicKey },
      {
        headers: {
          Authorization: `Basic ${base64Credentials}`,
        },
      }
    );

    const encryptedSecretKeyBase64 = response.data;
    const decryptedSecretKey = await decryptWithPEMPrivateKey(encryptedSecretKeyBase64, rsaPrivateKey);

    console.log("Decrypted secret key:", decryptedSecretKey);
    return decryptedSecretKey;
  } catch (error) {
    console.error("Error fetching secret key:", error);
    throw error;
  }
};

// Decrypt Secret Key with RSA Private Key
const decryptWithPEMPrivateKey = async (encryptedSecretKeyBase64, privateKeyPem) => {
  const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);
  const encryptedBytes = forge.util.decode64(encryptedSecretKeyBase64);
  return privateKey.decrypt(encryptedBytes);
};

export { fetchSecretKey, ensureRsaKeyReady };
